import { contentEnvHost, getContentEnvHost } from './environment';
import type { Env } from './types';

export const SANITY_NATIVE_CDN_HOST = 'cdn.sanity.io';

export function getSanityCdnHost(env: Env) {
  const contentHost = getContentEnvHost(env);

  return `sanity-cdn.${contentHost}`;
}

export function getSanityAssetFullUrl(src: string | null | undefined, env: Env): string {
  if (!src) return '';

  try {
    const url = new URL(src);

    if (url.host === SANITY_NATIVE_CDN_HOST) {
      url.host = getSanityCdnHost(env === 'local' ? 'dev' : env);
    }

    return url.toString();
  } catch (_) {
    // Just return whatever value was passed if was a non-valid URL string.
    return src;
  }
}

export type SanityImageOptions = {
  /** Rendered CSS width in px. Omit to leave the image unresized. */
  width?: number;
  /** Device pixel ratio to serve for. Multiplies `width`. */
  dpr?: number;
  quality?: number;
  format?: 'webp' | 'avif' | 'jpg' | 'png';
};

const isSanityImageHost = (host: string) =>
  host === SANITY_NATIVE_CDN_HOST || host.startsWith('sanity-cdn.');

/**
 * Builds a URL for a Sanity image asset that is both served from our own
 * Sanity CDN and resized/re-encoded by the image API.
 *
 * `getSanityAssetFullUrl` only swaps the hostname, which moves the request
 * behind our CloudFront but still transfers the full-size original. Use this
 * whenever the image is rendered at a known size — a 720x816 product PNG is
 * 484KB untransformed and 9.5KB at `w=180&fm=webp&q=90`, a ~50x saving.
 *
 * Non-image assets (`/files/...`) and non-Sanity hosts are returned with the
 * hostname swap only, since the image API does not apply to them.
 */
export function getSanityImageUrl(
  src: string | null | undefined,
  env: Env,
  options: SanityImageOptions = {},
): string {
  if (!src) return '';

  // An unset/unknown env would resolve to `sanity-cdn.undefined.com`, so leave
  // the URL alone rather than pointing it at a host that does not exist.
  if (!(env in contentEnvHost)) return src;

  const fullUrl = getSanityAssetFullUrl(src, env);
  if (!fullUrl) return '';

  const { width, dpr = 1, quality = 90, format = 'webp' } = options;

  try {
    const url = new URL(fullUrl);

    if (!isSanityImageHost(url.host) || !url.pathname.startsWith('/images/')) {
      return fullUrl;
    }

    if (width) url.searchParams.set('w', String(Math.round(width * dpr)));
    url.searchParams.set('fm', format);
    url.searchParams.set('q', String(quality));

    return url.toString();
  } catch (_) {
    return fullUrl;
  }
}

const DEFAULT_DPRS = [1, 2, 3];

/**
 * Builds a `srcSet` for a Sanity image, one candidate per device pixel ratio.
 *
 * Returns `undefined` when the URL is not a transformable Sanity image — a
 * srcSet whose candidates are all identical tells the browser nothing and only
 * makes the markup misleading. That is the common case for product images that
 * are hosted somewhere other than Sanity (e.g. scontent).
 */
export function getSanityImageSrcSet(
  src: string | null | undefined,
  env: Env,
  options: SanityImageOptions & { dprs?: number[] } = {},
): string | undefined {
  const { dprs = DEFAULT_DPRS, ...imageOptions } = options;
  if (!src || dprs.length === 0) return undefined;

  const candidates = dprs.map((dpr) => getSanityImageUrl(src, env, { ...imageOptions, dpr }));

  // No transform was applied, so every candidate is the same URL.
  if (new Set(candidates).size === 1) return undefined;

  return candidates.map((url, index) => `${url} ${dprs[index]}x`).join(', ');
}
